/**
 * TopNav - Horizontal top navigation layout (alternative to sidebar).
 */
import React from "react";
import { NavLink } from "react-router-dom";
import { ChevronRight, Sun, Moon, LogOut, Search as SearchIcon } from "lucide-react";
import NotificationBell from "../../../components/NotificationBell";
import UserAvatar from "../../../components/UserAvatar";
import { LayoutPickerButton, SpacingDensityPicker } from "./LayoutPickers";
import { PERMANENT_LOGO } from "./navConfig";

export function TopNav({
  user, branding, navItems, layout, onLayoutChange,
  showLayoutPicker, setShowLayoutPicker, isDark, toggleTheme,
  spacingDensity, onSpacingChange, onOpenSearch, onLogout,
}) {
  return (
    <header className="bg-slate-900 text-white flex-shrink-0 z-30">
      {/* Top row: logo + user */}
      <div className="flex items-center justify-between px-5 h-12 border-b border-slate-700/50">
        <div className="flex items-center gap-2.5">
          {branding.company_logo ? (
            <img src={branding.company_logo || PERMANENT_LOGO} alt="Logo" className="h-7 w-auto max-w-[100px] brightness-0 invert object-contain" />
          ) : (
            <>
              <div className="w-7 h-7 bg-emerald-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-[10px]">SG</span>
              </div>
              <span className="font-heading font-semibold text-sm">{branding.company_name}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={onOpenSearch}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-slate-400 bg-slate-800/60 hover:bg-slate-800 hover:text-slate-200 transition-colors border border-slate-700/40"
            data-testid="topnav-search-btn">
            <SearchIcon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Hledat</span>
            <kbd className="text-[9px] font-mono bg-slate-700/60 px-1 py-0.5 rounded text-slate-500 hidden sm:inline">&#8984;K</kbd>
          </button>
          <NotificationBell />
          <SpacingDensityPicker density={spacingDensity} onChange={onSpacingChange} dark />
          <LayoutPickerButton layout={layout} onChange={onLayoutChange}
            showLayoutPicker={showLayoutPicker} setShowLayoutPicker={setShowLayoutPicker} />
          <button onClick={toggleTheme} className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-700/50" data-testid="theme-toggle-btn">
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <a href="/" target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-emerald-400 flex items-center gap-1">
            Formulář <ChevronRight className="w-3 h-3" />
          </a>
          {user && (
            <div className="flex items-center gap-2 ml-2" data-testid="topnav-user">
              <UserAvatar user={user} size="sm" showTitle={false} />
              <span className="text-xs text-slate-300 hidden sm:inline">{user.name}</span>
            </div>
          )}
          <button onClick={onLogout}
            className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-700/50"
            data-testid="logout-btn">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Nav row: scrollable horizontal nav */}
      <nav className="flex items-center gap-0.5 px-4 h-10 overflow-x-auto scrollbar-hide">
        {navItems.map((item, idx) => {
          if (item.section === "divider") {
            return <div key={`div-${idx}`} className="w-px h-5 bg-slate-700/50 mx-1 flex-shrink-0" />;
          }
          const { path, label, icon: Icon, badge } = item;
          return (
            <NavLink key={path} to={path}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                  isActive ? "bg-emerald-600 text-white" : "text-slate-400 hover:bg-slate-700/50 hover:text-white"
                }`
              }
              data-testid={`nav-${label.toLowerCase().replace(/ /g, '-')}`}>
              <Icon className="w-3.5 h-3.5" />
              <span>{label}</span>
              {badge && <span className="text-[8px] font-bold bg-violet-500 text-white px-1 py-0.5 rounded-full leading-none">{badge}</span>}
            </NavLink>
          );
        })}
      </nav>
    </header>
  );
}
